package fyi.moko.hotpl.domain.usecase

import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.model.PlaceStatus
import fyi.moko.hotpl.domain.model.SyncState
import fyi.moko.hotpl.domain.model.Visibility
import fyi.moko.hotpl.domain.model.normalizeForComparison
import fyi.moko.hotpl.domain.repository.PlaceRepository
import kotlinx.coroutines.flow.first

/**
 * Merges local copies that can be identified as the same real place without guessing.
 *
 * Places with coordinates must have the same normalized name, owner, and exact coordinates.
 * Coordinate-less places may also merge when their normalized name and owner match and their
 * location descriptions do not conflict. Different non-blank location descriptions are kept
 * separate, protecting same-name branches such as restaurants in different neighborhoods.
 */
class MergeDuplicatePlacesUseCase(
    private val placeRepository: PlaceRepository,
    private val deletePlace: DeletePlaceUseCase,
) {
    data class Result(val groupsMerged: Int, val placesRemoved: Int)

    suspend operator fun invoke(): Result {
        val places = placeRepository.observePlaces().first().filter { !it.isDeleted }
        val visitCounts = placeRepository.observeVisitSummaries().first().associate { it.placeId to it.visitCount }
        val groups = duplicateGroups(places)

        var removed = 0
        for (group in groups) {
            val survivor = group.maxWithOrNull(
                compareBy<Place>(
                    { visitCounts[it.id] ?: 0 },
                    { placeCompleteness(it) },
                    { it.updatedAt },
                ),
            ) ?: continue

            placeRepository.savePlace(mergePlaceData(survivor, group))
            for (duplicate in group) {
                if (duplicate.id == survivor.id) continue
                placeRepository.observeVisits(duplicate.id).first().forEach { visit ->
                    placeRepository.saveVisitFromRemote(
                        visit.copy(
                            placeId = survivor.id,
                            syncState = SyncState.PENDING_UPSERT,
                            version = visit.version + 1,
                        ),
                    )
                }
                deletePlace(duplicate.id)
                removed++
            }
        }
        return Result(groupsMerged = groups.size, placesRemoved = removed)
    }
}

private data class OwnerNameKey(val ownerId: String, val normalizedName: String)
private data class CoordinateKey(val latitude: Double, val longitude: Double)

private fun duplicateGroups(places: List<Place>): List<List<Place>> = buildList {
    places.groupBy { OwnerNameKey(it.ownerId, normalizeForComparison(it.name)) }
        .values
        .filter { it.size > 1 }
        .forEach { sameOwnerAndName ->
            sameOwnerAndName
                .filter { it.latitude != null && it.longitude != null }
                .groupBy { CoordinateKey(it.latitude!!, it.longitude!!) }
                .values
                .filterTo(this) { it.size > 1 }

            val withoutCoordinates = sameOwnerAndName.filter {
                it.latitude == null && it.longitude == null
            }
            val withLocation = withoutCoordinates
                .filter { !it.locationContext.isNullOrBlank() }
                .groupBy { normalizeForComparison(it.locationContext.orEmpty()) }
            val withoutLocation = withoutCoordinates.filter { it.locationContext.isNullOrBlank() }

            if (withLocation.size <= 1) {
                val compatible = withLocation.values.flatten() + withoutLocation
                if (compatible.size > 1) add(compatible)
            } else {
                withLocation.values.filterTo(this) { it.size > 1 }
                if (withoutLocation.size > 1) add(withoutLocation)
            }
        }
}

private fun placeCompleteness(place: Place): Int =
    (if (!place.note.isNullOrBlank()) 1 else 0) +
        (if (!place.locationContext.isNullOrBlank()) 1 else 0) +
        place.tags.size +
        (if (place.imageFileName != null) 1 else 0) +
        (if (place.revisitCadence != null) 1 else 0) +
        (if (place.costFeel != null) 1 else 0) +
        (if (!place.phoneNumber.isNullOrBlank()) 1 else 0) +
        (if (!place.website.isNullOrBlank()) 1 else 0)

private fun mergePlaceData(survivor: Place, group: List<Place>): Place {
    val mergedTags = linkedMapOf<String, String>()
    group.flatMap { it.tags }.forEach { tag ->
        val normalizedTag = normalizeForComparison(tag)
        if (normalizedTag !in mergedTags) mergedTags[normalizedTag] = tag
    }
    return survivor.copy(
        note = survivor.note?.takeIf { it.isNotBlank() }
            ?: group.mapNotNull { it.note?.takeIf(String::isNotBlank) }.maxByOrNull(String::length),
        locationContext = survivor.locationContext?.takeIf { it.isNotBlank() }
            ?: group.firstNotNullOfOrNull { it.locationContext?.takeIf(String::isNotBlank) },
        tags = mergedTags.values.toList(),
        liked = group.any { it.liked },
        status = when {
            group.any { it.status == PlaceStatus.VISITED } -> PlaceStatus.VISITED
            group.any { it.status == PlaceStatus.WISHLIST } -> PlaceStatus.WISHLIST
            else -> PlaceStatus.DISMISSED
        },
        visibility = when {
            group.any { it.visibility == Visibility.PRIVATE } -> Visibility.PRIVATE
            group.any { it.visibility == Visibility.FRIENDS } -> Visibility.FRIENDS
            else -> Visibility.PUBLIC
        },
        revisitCadence = survivor.revisitCadence ?: group.firstNotNullOfOrNull { it.revisitCadence },
        costFeel = survivor.costFeel ?: group.firstNotNullOfOrNull { it.costFeel },
        phoneNumber = survivor.phoneNumber?.takeIf { it.isNotBlank() }
            ?: group.firstNotNullOfOrNull { it.phoneNumber?.takeIf(String::isNotBlank) },
        website = survivor.website?.takeIf { it.isNotBlank() }
            ?: group.firstNotNullOfOrNull { it.website?.takeIf(String::isNotBlank) },
    )
}
